import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaPlus } from 'react-icons/fa';
import { createApiClient } from '../../api/client';
import { useCaseContext } from '../context/CaseContext';
import { useUrgencyTabs, UrgencyTabBar, mapUrgencyToTab, CATEGORIES } from '../components/UrgencyTabs';
import ToggleSwitch from '../components/ToggleSwitch';
import '../mobile.css';

const MobileEvidenceList = ({ API_URL }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { fullFIR, setFullFIR } = useCaseContext();
  const fir = location.state?.fir || fullFIR;
  const isStatement = Boolean(location.state?.statement);

  const evidenceMap = location.state?.evidenceMap || fir?.evidenceList || {};
  const categoryMap = location.state?.evidenceCategory || fir?.evidenceCategory || {};
  const sourceMap = location.state?.evidenceSource || fir?.evidenceSource || {};
  const initialStatusMap = location.state?.evidenceStatusMap || fir?.evidenceStatus || {};

  const items = Object.keys(evidenceMap).map((description) => ({ description, relevance: evidenceMap[description] }));

  const [expandedKey, setExpandedKey] = useState(null);
  const [statusByKey, setStatusByKey] = useState(() => {
    const initial = {};

    items.forEach((item) => {
      const status = initialStatusMap[item.description];
      initial[item.description] =
        String(status).toLowerCase() === 'completed';
    });

    return initial;
  });

  const { activeTab, setActiveTab, hasUrgency, tabCounts, visibleItems } = useUrgencyTabs(items, (i) => categoryMap[i.description]);

  const pendingCounts = {};
  CATEGORIES.forEach((cat) => {
    pendingCounts[cat] = items
      .filter((i) => mapUrgencyToTab(categoryMap[i.description]) === cat)
      .filter((i) => statusByKey[i.description] !== true).length;
  });

  const toggleStatus = async (description) => {
    const prev = !!statusByKey[description];
    setStatusByKey((s) => ({ ...s, [description]: !prev }));

    try {
      const api = createApiClient(API_URL);
      const response = await api.post(`/fir/evidences/status/${fir.id}`, {
        evidence: description,
        status: !prev ? 'completed' : 'pending',
      });
      if (response.status === 200) setFullFIR(response.data);
    } catch {
      setStatusByKey((s) => ({ ...s, [description]: prev }));
    }
  };

  if (!fir) return <p style={{ textAlign: 'center', fontWeight: 700 }}>No case data available.</p>;

  if (items.length === 0) {
    return (
      <div style={{ position: 'relative', minHeight: '60vh' }}>
        <p style={{ textAlign: 'center', fontWeight: 700 }}>No evidence identified yet.</p>
        {!isStatement && (
          <button
            onClick={() => navigate('/m/fir/evidences/new', { state: { fir } })}
            className="m-chat-fab"
            style={{ right: '30px', bottom: 'calc(130px + env(safe-area-inset-bottom, 0px))', background: '#094268' }}
            aria-label="Add evidence"
          >
            <FaPlus size={22} color="#fff" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', minHeight: '60vh' }}>
      <UrgencyTabBar hasUrgency={hasUrgency} activeTab={activeTab} setActiveTab={setActiveTab} tabCounts={tabCounts} badgeCounts={pendingCounts} />

      {visibleItems.map((item) => {
        const isOpen = expandedKey === item.description;
        const isCompleted = !!statusByKey[item.description];

        return (
          <div key={item.description} className="m-card" onClick={() => setExpandedKey(isOpen ? null : item.description)} style={{ cursor: 'pointer' }}>
            <div style={{ fontSize: '14px', fontWeight: 600, color: '#111', textAlign: 'justify' }}>{item.description}</div>

            {isOpen && (
              <>
                {item.relevance && <div style={{ marginTop: '6px', textAlign: 'justify' }}><b>Relevance: </b>{item.relevance}</div>}
                <div style={{ marginTop: '6px', textAlign: 'justify' }}><b>Source: </b>{sourceMap[item.description] || 'FIR'}</div>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px' }}>
              {!isStatement ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <ToggleSwitch checked={isCompleted} onChange={() => toggleStatus(item.description)} />
                  <span style={{ fontSize: '13px', color: isCompleted ? '#4CAF50' : '#E53935' }}>
                    {isCompleted ? 'Completed' : 'Pending'}
                  </span>
                </div>
              ) : <span />}
              <span style={{ fontSize: '14px', fontWeight: 600, color: '#5B7CFF' }}>{isOpen ? 'Hide' : 'Show'}</span>
            </div>
          </div>
        );
      })}

      {!isStatement && (
        <button
          onClick={() => navigate('/m/fir/evidences/new', { state: { fir } })}
          className="m-chat-fab"
          style={{ right: '30px', bottom: 'calc(130px + env(safe-area-inset-bottom, 0px))', background: '#094268' }}
          aria-label="Add evidence"
        >
          <FaPlus size={22} color="#fff" />
        </button>
      )}
    </div>
  );
};

export default MobileEvidenceList;
